import {
    getFirestore, collection, query, where, documentId, getDocs, getDoc, doc, Timestamp, DocumentData
} from "firebase/firestore";
import { getClosestMonday } from "../helper";
import { get } from "lodash";
export interface Occupancy {
    entries: EntryData[];
    expectedEntries: ExpectedEntries[];
    date: string;
}

export interface EntryData {
    guest: number;
    vip: number;
    booking: number;
    tenantName: string;
}

export interface ExpectedEntries {
    bookingId: string;
    tenant: string;
    userId: string;
}

interface TenantAlerts {
    alerts: AlertsData[];
}

export interface AlertsData {
    alertTimestamp: string;
    entryTimestamp: string;
    cameraID: string;
    cameraName: string;
    cardholderID: string;
    client: string;
    cardholderName: string;
    gallagherID: string;
    location: string;
    plate: string;
    messageType: string;
    startTimestamp: string;
    repeatOffender: boolean;
    acknowledged: boolean;
    tenantID: string;
    alertType: string;
    description: string;
}

export const firestore = getFirestore();

export const occupancyCollection = collection(firestore, "occupancy");

export const getOccupancyPerWeek = async (): Promise<Occupancy[]> => {
    try {
        // create array to store document snapshot 
        const date = getClosestMonday(new Date());

        // create array that takes the date and adds 7 days to it
        const dateArray = [date];
        for (let i = 1; i < 7; i++) {
            // check if the date is after the current date
            const currentDate = new Date();
            const newDate = new Date(dateArray[i - 1]);
            if (newDate < currentDate) {
                dateArray.push(new Date(dateArray[i - 1].getTime() + 24 * 60 * 60 * 1000));
            }
        }

        const dates = query(
            occupancyCollection,
            where(documentId(), "in",
                dateArray.map((date) => date.toISOString().split("T")[0])
            ),
        );

        const snapshot = await getDocs(dates);

        return snapshot.docs.map((doc) => {
            const data = doc.data() as Occupancy;
            return {
                ...data,
                date: doc.data().date as string,
            };
        }
        );
    } catch (error) {
        console.error("Error fetching occupancy:", error);
        throw error;
    }
};

export const getEntriesPerWeek = async (): Promise<Occupancy[]> => {
    try {
        const date = getClosestMonday(new Date());

        const dateArray = [date];

        for (let i = 1; i < 7; i++) {
            dateArray.push(new Date(dateArray[i - 1].getTime() + 24 * 60 * 60 * 1000));
        }

        const dates = query(
            occupancyCollection,
            where(documentId(), "in", dateArray.map((date) => date.toISOString().split("T")[0]))
        );

        const snapshot = await getDocs(dates);

        return dateArray.map((date) => {
            const data = snapshot.docs.find((doc) => doc.id === date.toISOString().split("T")[0]);
            return {
                entries: get(data?.data(), "entries", []),
                expectedEntries: get(data?.data(), "expectedEntries", []),
                date: date.toISOString().split("T")[0],
            };
        });
    } catch (error) {
        console.error("Error fetching entries:", error);
        throw error;
    }
}

export const getTenantAlerts = async (tenant: string): Promise<AlertsData[]> => {
    try {
        const tenantDoc = doc(firestore, "alerts", tenant);


        const tenantSnapshot = await getDoc(tenantDoc);

        const tenantData = tenantSnapshot.data() as TenantAlerts;

        const sortedAlerts = tenantData.alerts.sort((a, b) => {
            return new Date(b.entryTimestamp).getTime() - new Date(a.entryTimestamp).getTime();
        });

        return sortedAlerts;

    } catch (error) {
        console.error("Error fetching tenant alerts:", error);
        throw error;
    }
}

export interface TenantReportSummary {
    totalBookings: number;
    completed: number;
    expired: number;
    avgPerUser: number;
    completionRate?: number;
}

export interface TenantReportUserStats {
    user: string;
    bookings: number;
    completed: number;
    expired: number;
    completionPercent?: number;
    lastCompleted?: string;
    completedDates: string[];
}

export interface TenantReport {
    id: string;
    tenant: string;
    period: string;
    summary: TenantReportSummary;
    users: TenantReportUserStats[];
    generatedBy?: string;
    generatedAt?: string;
    timezone?: string;
}

export const reportingCollection = collection(firestore, "reporting");

const buildReportDocumentId = (tenant: string, year: number, month: number) => {
    const paddedMonth = `${month}`.padStart(2, "0");
    return `${tenant}_${year}-${paddedMonth}`;
};

const expandMonthRange = (from: { month: number; year: number }, to: { month: number; year: number }) => {
    const cursor = new Date(from.year, from.month - 1, 1);
    const end = new Date(to.year, to.month - 1, 1);
    const months: { month: number; year: number }[] = [];

    while (cursor <= end) {
        months.push({ month: cursor.getMonth() + 1, year: cursor.getFullYear() });
        cursor.setMonth(cursor.getMonth() + 1);
    }

    return months;
};

const toNumber = (value: unknown, fallback = 0): number => {
    if (typeof value === "number" && !Number.isNaN(value)) {
        return value;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
};

const toDateOnlyString = (value: unknown): string => {
    if (value instanceof Timestamp) {
        return value.toDate().toISOString().split("T")[0];
    }
    if (value instanceof Date) {
        return value.toISOString().split("T")[0];
    }
    if (typeof value === "string") {
        return value;
    }
    return "";
};

const derivePeriodFromDocId = (docId: string) => {
    const parts = docId.split("_");
    if (parts.length >= 2) {
        const periodPart = parts[parts.length - 1];
        return periodPart;
    }
    return docId;
};

const parseUserStats = (raw: DocumentData): TenantReportUserStats | null => {
    const user = (raw?.user || raw?.name || raw?.userName || raw?.userId) as string | undefined;
    if (!user || typeof user !== "string") {
        return null;
    }

    const bookings = toNumber(raw?.bookings ?? raw?.totalBookings ?? raw?.total ?? 0);
    const completed = toNumber(raw?.completed ?? raw?.completedBookings ?? 0);
    const expired = toNumber(raw?.expired ?? raw?.expiredBookings ?? 0);
    const completionPercent = toNumber(
        raw?.completionPercent ?? raw?.completion ?? raw?.completionRate ?? (bookings ? (completed / bookings) * 100 : 0)
    );

    const completedDates = Array.isArray(raw?.completedDates) ? raw.completedDates.map(toDateOnlyString).filter(Boolean) : [];
    const lastCompleted = typeof raw?.lastCompleted === "string"
        ? raw.lastCompleted
        : completedDates.length > 0
            ? completedDates[completedDates.length - 1]
            : undefined;

    return {
        user,
        bookings,
        completed,
        expired,
        completionPercent,
        lastCompleted,
        completedDates,
    };
};

const parseTenantReport = (data: DocumentData, docId: string, tenant: string): TenantReport => {
    console.debug("[reports] parsing doc", docId, "data:", data);

    const summarySource = (data?.summary ?? data?.overview ?? data?.totals ?? {}) as Record<string, unknown>;

    const rawUserSource = (data?.perUserPerformance ?? data?.users ?? data?.userPerformance ?? data?.perUser ?? data?.byUser ?? []) as DocumentData[] | Record<string, DocumentData>;

    const usersArray = Array.isArray(rawUserSource)
        ? rawUserSource
        : typeof rawUserSource === "object" && rawUserSource !== null
            ? Object.entries(rawUserSource).map(([key, value]) => ({ user: key, ...(value as DocumentData) }))
            : [];

    const users = usersArray.map(parseUserStats).filter(Boolean) as TenantReportUserStats[];

    const derivedTotalBookings = users.reduce((sum, entry) => sum + entry.bookings, 0);
    const derivedCompleted = users.reduce((sum, entry) => sum + entry.completed, 0);
    const derivedExpired = users.reduce((sum, entry) => sum + entry.expired, 0);

    const summary: TenantReportSummary = {
        totalBookings: toNumber(summarySource?.totalBookings ?? summarySource?.total ?? summarySource?.bookings ?? derivedTotalBookings),
        completed: toNumber(summarySource?.completed ?? summarySource?.completedBookings ?? summarySource?.completedCount ?? derivedCompleted),
        expired: toNumber(summarySource?.expired ?? summarySource?.expiredBookings ?? summarySource?.expiredCount ?? derivedExpired),
        avgPerUser: toNumber(summarySource?.avgPerUser ?? summarySource?.averagePerUser ?? (users.length ? derivedTotalBookings / users.length : 0)),
        completionRate: summarySource?.completionRate !== undefined
            ? toNumber(summarySource.completionRate)
            : derivedTotalBookings > 0
                ? (derivedCompleted / derivedTotalBookings) * 100
                : undefined,
    };

    const generatedAtRaw = data?.generatedAt ?? data?.generatedOn ?? data?.computedAt;
    const generatedAt = generatedAtRaw ? toDateOnlyString(generatedAtRaw) || undefined : undefined;

    const timezone = typeof data?.timezone === "string" ? data.timezone : typeof data?.timeZone === "string" ? data.timeZone : undefined;

    const period = typeof data?.period === "string"
        ? data.period
        : typeof data?.month === "string"
            ? data.month
            : derivePeriodFromDocId(docId);

    return {
        id: docId,
        tenant,
        period,
        summary,
        users,
        generatedBy: typeof data?.generatedBy === "string" ? data.generatedBy : undefined,
        generatedAt,
        timezone,
    };
};

export const getTenantReportsForRange = async (
    tenant: string,
    from: { month: number; year: number },
    to: { month: number; year: number }
): Promise<TenantReport[]> => {
    try {
        const months = expandMonthRange(from, to);
        console.debug("[reports] fetching reporting docs for", tenant, "months:", months);

        const snapshots = await Promise.all(months.map(async ({ month, year }) => {
            const docId = buildReportDocumentId(tenant, year, month);
            const reportDoc = doc(reportingCollection, docId);
            const reportSnapshot = await getDoc(reportDoc);
            console.debug("[reports] checked doc", docId, "exists:", reportSnapshot.exists());
            if (!reportSnapshot.exists()) {
                return null;
            }
            const docData = reportSnapshot.data();
            console.debug("[reports] raw doc data", docId, docData);
            return parseTenantReport(docData, docId, tenant);
        }));

        return snapshots.filter((item): item is TenantReport => item !== null);
    } catch (error) {
        console.error("Error fetching tenant reporting data:", error);
        throw error;
    }
};

